import { type TriageCandidate, createPrioritizationProjection } from '@portfolio-pm/projection';
import {
  type PrioritizationContext,
  type TaskRecommendation,
  deriveDaypart,
} from '@portfolio-pm/scoring';
import type { CommitmentStateT, EnergyT, TaskActionabilityT } from '@portfolio-pm/shared';
import {
  getDailyCapacity,
  listProtectedBlocks,
  recordEnrichmentEvent,
  recordFieldEvidence,
  setDailyCapacity,
  setTriageDisposition,
  updateInitiative,
} from '@portfolio-pm/yjs-doc';
import { CircleDashed, CircleHelp, Clock3, Flag, Link2Off, ListTodo, Target } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useNow } from '../../lib/useNow.ts';
import { getDocStore } from '../../store/doc-store.ts';
import { ScrollViewport } from '../layout/ScrollViewport.tsx';
import { TaskDetailPanel } from '../tasks/TaskDetailPanel.tsx';
import type { TaskWorkspaceView } from '../tasks/task-workspace-model.ts';

type FocusTab = 'today' | 'stewardship' | 'clarify';

function localDate(now: number, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

export function FocusWorkspace(): JSX.Element {
  const store = getDocStore();
  const snapshot = store.useFocusSnapshot();
  const projects = store.useProjects();
  const allProjects = store.useAllProjects();
  const now = useNow();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const date = localDate(now, timezone);
  const [tab, setTab] = useState<FocusTab>('today');
  const [energy, setEnergy] = useState<EnergyT>('med');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [activeTaskId, setActiveTaskId] = useState<string | undefined>();
  const titleFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const prioritization = useMemo(
    () => createPrioritizationProjection(store.doc, store.projection),
    [store],
  );
  useEffect(() => () => prioritization.destroy(), [prioritization]);
  const revision = useSyncExternalStore(
    prioritization.subscribe,
    prioritization.getRevision,
    prioritization.getRevision,
  );
  const capacity = getDailyCapacity(store.doc, date, timezone);
  const context: PrioritizationContext = useMemo(
    () => ({
      version: 1,
      now,
      localDate: date,
      timezone,
      currentEnergy: energy,
      currentDaypart: deriveDaypart(now),
      projectionRevision: revision,
      ...(capacity ? { capacity } : {}),
      protectedBlocks: listProtectedBlocks(store.doc).filter(
        (block) => block.localDate === date && block.timezone === timezone,
      ),
    }),
    [capacity, date, energy, now, revision, store.doc, timezone],
  );
  const recommendations = useMemo(
    () => prioritization.recommendations(context),
    [context, prioritization],
  );
  const stewardship = prioritization.stewardship(now);
  const candidates = prioritization.candidates(context, 50).items;

  const eligible = recommendations.filter((item) => item.eligibility === 'eligible');
  const provisional = recommendations.filter((item) => item.eligibility === 'provisional');
  const rankedTasks = [...eligible, ...provisional].map((item) => item.task);
  const activeTask = activeTaskId
    ? rankedTasks.find((task) => task.id === activeTaskId)
    : undefined;
  const focusView: TaskWorkspaceView = { kind: 'system', id: 'today' };

  useEffect(() => {
    if (activeTaskId && !rankedTasks.some((task) => task.id === activeTaskId)) {
      setActiveTaskId(undefined);
    }
  }, [activeTaskId, rankedTasks]);

  const toggleSelection = (candidate: TriageCandidate): void => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(candidate.entityId)) next.delete(candidate.entityId);
      else next.add(candidate.entityId);
      return next;
    });
  };

  const parkSelected = (): void => {
    const targets = candidates.filter(
      (candidate) => selected.has(candidate.entityId) && candidate.entityType === 'task',
    );
    if (!targets.length || !window.confirm(`Park ${targets.length} selected task(s)?`)) return;
    for (const target of targets)
      store.updateTask(target.entityId, { commitmentState: 'later_parked' });
    setSelected(new Set());
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden text-[color:var(--app-text)]">
      <header className="shrink-0 border-b border-[color:var(--app-border)] px-4 py-4 md:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="eyebrow-label">Deterministic prioritization</p>
            <h1 className="text-2xl font-semibold">Focus</h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <span>Energy</span>
              <select
                aria-label="Current energy"
                className="ui-select"
                value={energy}
                onChange={(event) => setEnergy(event.target.value as EnergyT)}
              >
                <option value="low">Low</option>
                <option value="med">Medium</option>
                <option value="high">High</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <span>Today’s capacity</span>
              <input
                aria-label="Today's capacity in minutes"
                className="ui-input w-24"
                type="number"
                min={0}
                step={15}
                value={capacity?.availableMinutes ?? ''}
                placeholder="minutes"
                onChange={(event) => {
                  const value = Number(event.target.value);
                  if (Number.isInteger(value) && value >= 0) {
                    setDailyCapacity(store.doc, {
                      localDate: date,
                      timezone,
                      availableMinutes: value,
                    });
                  }
                }}
              />
            </label>
          </div>
        </div>
        <nav aria-label="Focus views" className="mt-4 flex gap-2">
          {(['today', 'stewardship', 'clarify'] as const).map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={tab === value}
              onClick={() => setTab(value)}
              className={
                tab === value ? 'ui-button ui-button-primary' : 'ui-button ui-button-subtle'
              }
            >
              {value === 'today' ? 'Today' : value === 'stewardship' ? 'Stewardship' : 'Clarify'}
              {value === 'clarify' && candidates.length ? ` (${candidates.length})` : ''}
            </button>
          ))}
        </nav>
      </header>

      {tab !== 'today' && (
        <ScrollViewport className="min-h-0 flex-1" contentClassName="space-y-4 p-4 md:p-6">
          {tab === 'stewardship' && (
            <section aria-labelledby="stewardship-heading" className="surface-panel p-4">
              <h2 id="stewardship-heading" className="text-lg font-semibold">
                Weekly stewardship
              </h2>
              <p className="mt-1 text-sm text-[color:var(--app-text-muted)]">
                Important objectives and initiatives that need movement, review, decomposition, or
                explicit parking.
              </p>
              <div className="mt-4 grid gap-3 lg:grid-cols-2">
                {stewardship.map((item) => (
                  <article
                    key={`${item.entityType}:${item.entityId}`}
                    className="rounded-xl border border-[color:var(--app-border)] p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs uppercase text-[color:var(--app-text-muted)]">
                          {item.entityType}
                        </p>
                        <h3 className="font-medium">{item.title}</h3>
                      </div>
                      <span className={item.stalled ? 'ui-chip ui-chip-danger' : 'ui-chip'}>
                        {item.stalled
                          ? 'Cadence missed'
                          : (item.importance ?? 'Importance unknown')}
                      </span>
                    </div>
                    <p className="mt-3 text-sm text-[color:var(--app-text-muted)]">
                      {item.lastMovementAt
                        ? `Last reliable movement ${new Date(item.lastMovementAt).toLocaleDateString()}.`
                        : 'No reliable movement evidence is stored.'}
                    </p>
                    {item.reasonCodes.length > 0 && <ReasonCodes codes={item.reasonCodes} />}
                  </article>
                ))}
                {stewardship.length === 0 && (
                  <p className="text-sm text-[color:var(--app-text-muted)]">
                    No objectives or initiatives yet.
                  </p>
                )}
              </div>
            </section>
          )}

          {tab === 'clarify' && (
            <section aria-labelledby="clarify-heading" className="surface-panel p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 id="clarify-heading" className="text-lg font-semibold">
                    Clarification queue
                  </h2>
                  <p className="mt-1 text-sm text-[color:var(--app-text-muted)]">
                    One small question at a time; skipping preserves the unknown.
                  </p>
                </div>
                <button
                  type="button"
                  className="ui-button ui-button-subtle"
                  disabled={!selected.size}
                  onClick={parkSelected}
                >
                  Park selected ({selected.size})
                </button>
              </div>
              <div className="mt-4 space-y-3">
                {candidates.map((candidate) => (
                  <ClarifyCard
                    key={`${candidate.entityType}:${candidate.entityId}:${candidate.sourceFingerprint}`}
                    candidate={candidate}
                    selected={selected.has(candidate.entityId)}
                    onToggle={() => toggleSelection(candidate)}
                    onTaskPatch={(patch) => store.updateTask(candidate.entityId, patch)}
                    onEntityPatch={(patch) => {
                      if (candidate.entityType === 'task')
                        store.updateTask(candidate.entityId, patch);
                      else if (candidate.entityType === 'objective')
                        store.updateObjective(candidate.entityId, patch);
                      else updateInitiative(store.doc, candidate.entityId, patch);
                    }}
                    objectives={store.projection.getObjectives()}
                    initiatives={prioritization.getInitiatives()}
                    onSkip={() =>
                      setTriageDisposition(store.doc, {
                        entityType: candidate.entityType,
                        entityId: candidate.entityId,
                        disposition: 'skipped',
                        reasonCodes: candidate.reasonCodes,
                        sourceFingerprint: candidate.sourceFingerprint,
                        actor: 'local-user',
                        tool: 'focus-clarify',
                      })
                    }
                    onLeaveUnknown={(field) => {
                      recordFieldEvidence(store.doc, {
                        entityType: candidate.entityType,
                        entityId: candidate.entityId,
                        field,
                        valueState: 'explicit_unknown',
                        source: 'user_confirmed',
                        actor: 'local-user',
                        tool: 'focus-clarify',
                      });
                      recordEnrichmentEvent(store.doc, {
                        entityType: candidate.entityType,
                        entityId: candidate.entityId,
                        action: 'left_unknown',
                        changes: [{ field, valueState: 'explicit_unknown' }],
                        actor: 'local-user',
                        tool: 'focus-clarify',
                      });
                      setTriageDisposition(store.doc, {
                        entityType: candidate.entityType,
                        entityId: candidate.entityId,
                        disposition: 'skipped',
                        reasonCodes: candidate.reasonCodes,
                        sourceFingerprint: candidate.sourceFingerprint,
                        actor: 'local-user',
                        tool: 'focus-clarify',
                      });
                    }}
                    onDefer={() =>
                      setTriageDisposition(store.doc, {
                        entityType: candidate.entityType,
                        entityId: candidate.entityId,
                        disposition: 'deferred',
                        reasonCodes: candidate.reasonCodes,
                        sourceFingerprint: candidate.sourceFingerprint,
                        resurfaceAt: now + 7 * 86_400_000,
                        actor: 'local-user',
                        tool: 'focus-clarify',
                      })
                    }
                    onReject={() => {
                      setTriageDisposition(store.doc, {
                        entityType: candidate.entityType,
                        entityId: candidate.entityId,
                        disposition: 'rejected',
                        reasonCodes: candidate.reasonCodes,
                        sourceFingerprint: candidate.sourceFingerprint,
                        actor: 'local-user',
                        tool: 'focus-clarify',
                      });
                      recordEnrichmentEvent(store.doc, {
                        entityType: candidate.entityType,
                        entityId: candidate.entityId,
                        action: 'rejected',
                        actor: 'local-user',
                        tool: 'focus-clarify',
                      });
                    }}
                    onDiscard={() => {
                      if (candidate.entityType !== 'task') return;
                      const typed = window.prompt(
                        `Type ${candidate.entityId} to move this task to Trash.`,
                      );
                      if (typed === candidate.entityId) store.removeTask(candidate.entityId);
                    }}
                  />
                ))}
                {candidates.length === 0 && (
                  <p className="text-sm text-[color:var(--app-text-muted)]">
                    Nothing needs clarification right now.
                  </p>
                )}
              </div>
            </section>
          )}
        </ScrollViewport>
      )}
      {tab === 'today' && (
        <div className="task-workspace-grid relative flex h-full min-h-0 flex-col overflow-hidden bg-[color:var(--app-workspace)] md:grid">
          <section
            data-testid="focus-ranked-list-pane"
            className="min-h-0 overflow-hidden border-r border-[color:var(--app-border)] bg-white"
          >
            <FocusRankedList
              eligible={eligible}
              provisional={provisional}
              activeTaskId={activeTaskId}
              onSelect={setActiveTaskId}
            />
          </section>
          <div aria-hidden="true" className="hidden bg-[color:var(--app-border)] md:block" />
          <div className="task-detail-stage hidden h-full min-h-0 flex-col bg-white md:flex">
            <TaskDetailPanel
              projects={projects}
              workflowColumns={snapshot.workflowColumns}
              selectedTask={activeTask}
              summaryTasks={rankedTasks}
              summaryCompletedCount={
                rankedTasks.filter((task) => task.completedAt !== undefined).length
              }
              allTasks={snapshot.tasks}
              taskActivities={snapshot.taskActivities}
              projectsById={new Map(allProjects.map((project) => [project.id, project]))}
              titleFieldRef={titleFieldRef}
              currentView={focusView}
              viewTitle="Ranked focus"
              viewDescription="Tasks in deterministic priority order for today."
              onChangeView={() => undefined}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function FocusRankedList({
  eligible,
  provisional,
  activeTaskId,
  onSelect,
}: {
  eligible: TaskRecommendation[];
  provisional: TaskRecommendation[];
  activeTaskId: string | undefined;
  onSelect(taskId: string): void;
}): JSX.Element {
  return (
    <ScrollViewport className="h-full min-h-0" contentClassName="py-3">
      <FocusRankedGroup
        title="Ready for today"
        description="Ranked actions with enough known context to begin."
        items={eligible}
        empty="No ready actions fit the known context."
        activeTaskId={activeTaskId}
        onSelect={onSelect}
      />
      <FocusRankedGroup
        title="Provisional"
        description="Useful possibilities held apart until a material fact is known."
        items={provisional}
        empty="No provisional actions."
        activeTaskId={activeTaskId}
        onSelect={onSelect}
      />
    </ScrollViewport>
  );
}

function FocusRankedGroup({
  title,
  description,
  items,
  empty,
  activeTaskId,
  onSelect,
}: {
  title: string;
  description: string;
  items: TaskRecommendation[];
  empty: string;
  activeTaskId: string | undefined;
  onSelect(taskId: string): void;
}): JSX.Element {
  return (
    <section
      aria-labelledby={`${title.toLowerCase().replaceAll(' ', '-')}-heading`}
      className="mb-5"
    >
      <div className="border-y border-[color:var(--app-border)] bg-[color:var(--app-surface-muted)] px-4 py-2.5">
        <div className="flex items-baseline justify-between gap-3">
          <h2
            id={`${title.toLowerCase().replaceAll(' ', '-')}-heading`}
            className="text-sm font-semibold"
          >
            {title}
          </h2>
          <span className="text-xs text-[color:var(--app-text-muted)]">{items.length}</span>
        </div>
        <p className="mt-0.5 text-xs text-[color:var(--app-text-muted)]">{description}</p>
      </div>
      {items.length ? (
        <ol>
          {items.map((item, index) => (
            <li key={item.task.id} className="border-b border-[color:var(--app-border)]">
              <button
                type="button"
                aria-pressed={activeTaskId === item.task.id}
                onClick={() => onSelect(item.task.id)}
                className={`grid w-full grid-cols-[2.25rem_minmax(0,1fr)_auto] items-center gap-2 px-4 py-3 text-left transition-colors active:translate-y-px ${
                  activeTaskId === item.task.id
                    ? 'bg-[color:var(--app-accent-soft)]'
                    : 'bg-[color:var(--app-workspace)] hover:bg-[color:var(--app-surface-muted)]'
                }`}
              >
                <span className="text-xs font-medium tabular-nums text-[color:var(--app-text-muted)]">
                  {index + 1}
                </span>
                <span className="min-w-0">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-sm font-medium text-[color:var(--app-text)]">
                      {item.task.title}
                    </span>
                    {item.tier && (
                      <span className="hidden shrink-0 text-xs text-[color:var(--app-text-muted)] lg:inline">
                        {item.tier.replaceAll('_', ' ')}
                      </span>
                    )}
                  </span>
                  <span className="mt-1 flex flex-wrap items-center gap-1.5">
                    <MissingSignals codes={item.completeness.reasonCodes} />
                    {item.task.tags.slice(0, 2).map((tag) => (
                      <span key={tag} className="ui-chip text-xs">
                        #{tag}
                      </span>
                    ))}
                  </span>
                </span>
                <span
                  title={item.contributions.map((contribution) => contribution.detail).join(' · ')}
                  className="ui-chip shrink-0 whitespace-nowrap text-xs font-medium"
                >
                  {item.score} points
                </span>
              </button>
            </li>
          ))}
        </ol>
      ) : (
        <p className="px-4 py-4 text-sm text-[color:var(--app-text-muted)]">{empty}</p>
      )}
    </section>
  );
}

const MISSING_SIGNAL_ICONS: Record<string, typeof CircleHelp> = {
  missing_objective_or_initiative_link: Link2Off,
  missing_estimate: Clock3,
  missing_expected_outcome: Flag,
  missing_commitment_state: CircleDashed,
  missing_actionability: ListTodo,
  missing_objective: Target,
};

function MissingSignals({ codes }: { codes: readonly string[] }): JSX.Element | null {
  if (!codes.length) return null;
  return (
    <span className="flex items-center gap-1" aria-label="Missing prioritization context">
      {codes.map((code) => {
        const Icon = MISSING_SIGNAL_ICONS[code] ?? CircleHelp;
        const label = code.replace(/^missing_/, 'Missing ').replaceAll('_', ' ');
        return (
          <span
            key={code}
            title={label}
            aria-label={label}
            className="inline-flex h-5 w-5 items-center justify-center rounded border border-[color:var(--app-warning-border)] bg-[color:var(--app-warning-soft)] text-[color:var(--app-warning)]"
          >
            <Icon aria-hidden="true" size={12} />
            <span className="sr-only">{label}</span>
          </span>
        );
      })}
    </span>
  );
}

function ReasonCodes({ codes }: { codes: readonly string[] }): JSX.Element {
  return (
    <ul className="mt-3 flex flex-wrap gap-2" aria-label="Reasons">
      {codes.map((code) => (
        <li key={code} className="ui-chip text-xs">
          {code.replace(/_/g, ' ')}
        </li>
      ))}
    </ul>
  );
}

function ClarifyCard({
  candidate,
  selected,
  onToggle,
  onTaskPatch,
  onEntityPatch,
  objectives,
  initiatives,
  onSkip,
  onLeaveUnknown,
  onDefer,
  onReject,
  onDiscard,
}: {
  candidate: TriageCandidate;
  selected: boolean;
  onToggle: () => void;
  onTaskPatch: (patch: Record<string, unknown>) => void;
  onEntityPatch: (patch: Record<string, unknown>) => void;
  objectives: Array<{ id: string; title: string }>;
  initiatives: Array<{ id: string; title: string }>;
  onSkip: () => void;
  onLeaveUnknown: (field: string) => void;
  onDefer: () => void;
  onReject: () => void;
  onDiscard: () => void;
}): JSX.Element {
  const task = candidate.recommendation?.task;
  const firstReason = candidate.reasonCodes[0];
  const [text, setText] = useState('');
  return (
    <article className="rounded-xl border border-[color:var(--app-border)] p-4">
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          aria-label={`Select ${candidate.title}`}
          checked={selected}
          onChange={onToggle}
        />
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase text-[color:var(--app-text-muted)]">
            {candidate.entityType}
          </p>
          <h3 className="font-medium">{candidate.title}</h3>
          <ReasonCodes codes={candidate.reasonCodes} />
          {task && firstReason === 'missing_commitment_state' && (
            <ChoiceQuestion
              label="Is this an active commitment?"
              choices={[
                ['Active now', 'active_now'],
                ['Waiting', 'waiting'],
                ['Park for later', 'later_parked'],
                ['Someday', 'someday'],
              ]}
              onChoose={(value) => onTaskPatch({ commitmentState: value as CommitmentStateT })}
            />
          )}
          {task && firstReason === 'missing_actionability' && (
            <ChoiceQuestion
              label="Is this a concrete next action?"
              choices={[
                ['Yes, concrete', 'concrete'],
                ['Needs breaking down', 'needs_decomposition'],
                ['Placeholder', 'placeholder'],
              ]}
              onChoose={(value) => onTaskPatch({ actionability: value as TaskActionabilityT })}
            />
          )}
          {task && firstReason === 'missing_estimate' && (
            <ChoiceQuestion
              label="Roughly how long will it take?"
              choices={[
                ['15 min', '15'],
                ['30 min', '30'],
                ['60 min', '60'],
                ['2 hours', '120'],
                ['4+ hours', '240'],
              ]}
              onChoose={(value) => onTaskPatch({ estimateMinutes: Number(value) })}
            />
          )}
          {task &&
            (firstReason === 'missing_expected_outcome' ||
              firstReason === 'missing_waiting_trigger') && (
              <div className="mt-4">
                <label className="text-sm font-medium" htmlFor={`clarify-${candidate.entityId}`}>
                  {firstReason === 'missing_waiting_trigger'
                    ? 'What will make this available?'
                    : 'What should be true when this is done?'}
                </label>
                <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                  <input
                    id={`clarify-${candidate.entityId}`}
                    className="ui-input flex-1"
                    value={text}
                    onChange={(event) => setText(event.target.value)}
                  />
                  <button
                    type="button"
                    className="ui-button ui-button-primary"
                    disabled={!text.trim()}
                    onClick={() =>
                      onTaskPatch(
                        firstReason === 'missing_waiting_trigger'
                          ? { availabilityTrigger: text.trim() }
                          : { expectedOutcome: text.trim() },
                      )
                    }
                  >
                    Save
                  </button>
                </div>
              </div>
            )}
          {(firstReason === 'missing_outcome' || firstReason === 'missing_success_definition') && (
            <div className="mt-4">
              <label className="text-sm font-medium" htmlFor={`clarify-${candidate.entityId}`}>
                {firstReason === 'missing_outcome'
                  ? 'What outcome should this objective produce?'
                  : 'How will we know this initiative succeeded?'}
              </label>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                <input
                  id={`clarify-${candidate.entityId}`}
                  className="ui-input flex-1"
                  value={text}
                  onChange={(event) => setText(event.target.value)}
                />
                <button
                  type="button"
                  className="ui-button ui-button-primary"
                  disabled={!text.trim()}
                  onClick={() =>
                    onEntityPatch(
                      firstReason === 'missing_outcome'
                        ? { outcome: text.trim() }
                        : { successDefinition: text.trim() },
                    )
                  }
                >
                  Save
                </button>
              </div>
            </div>
          )}
          {firstReason === 'missing_importance' && (
            <ChoiceQuestion
              label="How strongly should stewardship protect this?"
              choices={[
                ['Standard', 'standard'],
                ['Important', 'important'],
                ['Protected cadence', 'protected'],
              ]}
              onChoose={(value) => onEntityPatch({ importance: value })}
            />
          )}
          {firstReason === 'missing_progress_cadence' && (
            <ChoiceQuestion
              label="How often should meaningful progress occur?"
              choices={[
                ['Weekly', '7'],
                ['Every two weeks', '14'],
                ['Monthly', '30'],
              ]}
              onChoose={(value) => onEntityPatch({ minimumProgressCadenceDays: Number(value) })}
            />
          )}
          {task && firstReason === 'missing_objective_or_initiative_link' && (
            <label className="mt-4 block text-sm font-medium">
              Relevant objective or initiative
              <select
                className="ui-select mt-2 w-full"
                defaultValue=""
                onChange={(event) => {
                  const [kind, id] = event.target.value.split(':');
                  if (kind === 'objective') onTaskPatch({ objectiveId: id });
                  if (kind === 'initiative') onTaskPatch({ initiativeId: id });
                }}
              >
                <option value="">Choose only if relevant</option>
                {objectives.map((item) => (
                  <option key={`objective:${item.id}`} value={`objective:${item.id}`}>
                    Objective: {item.title}
                  </option>
                ))}
                {initiatives.map((item) => (
                  <option key={`initiative:${item.id}`} value={`initiative:${item.id}`}>
                    Initiative: {item.title}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" className="ui-button ui-button-subtle" onClick={onSkip}>
              Skip
            </button>
            <button type="button" className="ui-button ui-button-subtle" onClick={onDefer}>
              Defer 7 days
            </button>
            <button type="button" className="ui-button ui-button-subtle" onClick={onReject}>
              Reject inference
            </button>
            {firstReason && (
              <button
                type="button"
                className="ui-button ui-button-subtle"
                onClick={() => onLeaveUnknown(firstReason.replace(/^missing_/, ''))}
              >
                Leave unknown
              </button>
            )}
            {task && (
              <button
                type="button"
                className="ui-button ui-button-subtle"
                onClick={() => onTaskPatch({ commitmentState: 'later_parked' })}
              >
                Park
              </button>
            )}
            {task && (
              <button type="button" className="ui-button ui-button-danger" onClick={onDiscard}>
                Move to Trash
              </button>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

function ChoiceQuestion({
  label,
  choices,
  onChoose,
}: { label: string; choices: string[][]; onChoose: (value: string) => void }): JSX.Element {
  return (
    <fieldset className="mt-4">
      <legend className="text-sm font-medium">{label}</legend>
      <div className="mt-2 flex flex-wrap gap-2">
        {choices.map(([name, value]) => (
          <button
            key={value}
            type="button"
            className="ui-button ui-button-subtle"
            onClick={() => onChoose(value ?? '')}
          >
            {name}
          </button>
        ))}
      </div>
    </fieldset>
  );
}
